// Video source resolution for the PRYMEBET live-blackjack video layer.
//
// Maps a table's video config — either a legacy `stream_url` string or the
// explicit provider fields — into a normalized descriptor the LiveVideoPlayer
// renders.
//
// Providers: webrtc (primary) | hls | youtube | twitch | iframe (legacy)

const SAFE_SCHEMES = ["http:", "https:"];

// Parse a URL and reject anything that isn't http(s) — blocks javascript:,
// data:, file: and other unsafe schemes at the boundary.
function safeUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  try {
    const u = new URL(raw.trim());
    if (!SAFE_SCHEMES.includes(u.protocol)) return null;
    return u;
  } catch {
    return null;
  }
}

// WHEP playback endpoint. Detection is transport-based, not vendor-branded.
export function isWhepEndpoint(u) {
  if (!u) return false;
  return /\/webRTC\/play\/?$/i.test(u.pathname);
}

// Legacy detection preserved verbatim from the original LiveDealerVideo:
// YouTube / Twitch / HLS (.m3u8) / generic iframe.
function parseLegacy(u, rawUrl) {
  const host = u.hostname.toLowerCase();

  if (host.includes("youtube.com") || host.includes("youtu.be")) {
    let id = "";
    if (host.includes("youtu.be")) id = u.pathname.split("/").filter(Boolean)[0] || "";
    else if (u.pathname.includes("/embed/")) id = u.pathname.split("/embed/")[1]?.split("/")[0] || "";
    else if (u.pathname.includes("/live/")) id = u.pathname.split("/live/")[1]?.split("/")[0] || "";
    else id = u.searchParams.get("v") || "";
    if (!id) return null;
    return {
      provider: "youtube",
      kind: "iframe",
      label: "YouTube",
      src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0`,
    };
  }

  if (host.includes("twitch.tv")) {
    const channel = u.pathname.split("/").filter(Boolean).pop();
    if (!channel) return null;
    const parent = typeof window !== "undefined" ? window.location.hostname : "localhost";
    return {
      provider: "twitch",
      kind: "iframe",
      label: "Twitch",
      src: `https://player.twitch.tv/?channel=${channel}&parent=${parent}&autoplay=true&muted=true`,
    };
  }

  if (u.pathname.toLowerCase().endsWith(".m3u8")) {
    return { provider: "hls", kind: "hls", label: "HLS", src: rawUrl };
  }

  return { provider: "iframe", kind: "iframe", label: "Live", src: rawUrl };
}

// Public contract. Accepts a legacy string or a config object:
//   { url|stream_url, video_provider, fallback_stream_url }
// Returns { primary, fallback } normalized sources (or nulls).
export function resolveVideoSource(config) {
  const cfg = typeof config === "string" ? { url: config } : (config || {});
  const rawUrl = (cfg.url || cfg.stream_url || "").trim();
  const explicit = String(cfg.video_provider || "auto").toLowerCase();
  const u = safeUrl(rawUrl);

  let primary = null;
  if (u) {
    if (explicit === "webrtc" || (explicit === "auto" && isWhepEndpoint(u))) {
      primary = { provider: "webrtc", kind: "webrtc", label: "WebRTC", src: rawUrl };
    } else if (explicit === "hls") {
      primary = { provider: "hls", kind: "hls", label: "HLS", src: rawUrl };
    } else if (explicit === "youtube" || explicit === "twitch" || explicit === "iframe") {
      primary = parseLegacy(u, rawUrl) || { provider: explicit, kind: "iframe", label: "Live", src: rawUrl };
    } else {
      primary = parseLegacy(u, rawUrl);
    }
  }

  // Fallback must be explicit. Low-latency WHEP playback is paired with WHIP
  // ingest; do not assume the same live input also exposes an HLS manifest.
  // PRYMEBET currently uses an explicit Twitch fallback.
  let fallback = null;
  const fu = safeUrl(cfg.fallback_stream_url);
  if (fu) {
    fallback = parseLegacy(fu, cfg.fallback_stream_url.trim());
  }

  return { primary, fallback };
}

export const SUPPORTED_PROVIDERS = ["auto", "webrtc", "hls", "youtube", "twitch", "iframe"];
